import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Mail, Phone, User, Server, MoreVertical, Plus } from "lucide-react";
import CustomerFormModal from "../components/CustomerFormModal";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [modelCountsByCustomer, setModelCountsByCustomer] = useState({});
  const [modelCounts, setModelCounts] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  async function fetchCustomers() {
    try {
      const res = await fetch(`${API_BASE}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const data = JSON.parse(text);
      setCustomers(data.customers || data || []);
    } catch (err) {
      console.error("❌ Fetch customers error:", err);
      setCustomers([]);
    }
  }

  useEffect(() => {
    fetchCustomers();
  }, [API_BASE, token]);


  // Müşteriler
  useEffect(() => {
    const url = `${API_BASE}/customers`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          setCustomers(data.customers || data || []);
        } catch (err) {
          console.error("❌ JSON parse error:", err);
          setCustomers([]);
        }
      })
      .catch((err) => {
        console.error("❌ Fetch customers error:", err);
        setCustomers([]);
      });
  }, [API_BASE, token]);

  // Tüm gatewayler (model bazlı özet için)
  useEffect(() => {
    async function fetchGateways() {
      try {
        const res = await fetch(`${API_BASE}/gateways/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const counts = data.reduce((acc, gw) => {
          const model = gw.model || "Bilinmiyor";
          acc[model] = (acc[model] || 0) + 1;
          return acc;
        }, {});
        setModelCounts(counts);
      } catch (err) {
        console.error("❌ Fetch gateways error:", err);
        setModelCounts({});
      }
    }
    fetchGateways();
  }, [API_BASE, token]);

  // Her müşteri için gateway+cihaz sayıları
  useEffect(() => {
    async function fetchCustomerGateways(customerId) {
      try {
        const res = await fetch(`${API_BASE}/gateways/by-customer/${customerId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const grouped = data.reduce((acc, gw) => {
          const model = gw.model || "Bilinmiyor";
          if (!acc[model]) acc[model] = { gateways: 0, devices: 0 };
          acc[model].gateways += 1;
          acc[model].devices += Number(gw.device_count || 0);
          return acc;
        }, {});

        setModelCountsByCustomer((prev) => ({
          ...prev,
          [customerId]: grouped,
        }));
      } catch (err) {
        console.error(`❌ Fetch customer ${customerId} gateways error:`, err);
      }
    }

    customers.forEach((c) => {
      if (!modelCountsByCustomer[c.id]) {
        fetchCustomerGateways(c.id);
      }
    });
  }, [customers, API_BASE, token]);

async function handleResetPassword(customer) {
  const newPassword = window.prompt(
    `${customer.username} için yeni şifreyi gir:`
  );

  if (!newPassword || newPassword.length < 6) {
    alert("Şifre en az 6 karakter olmalı");
    return;
  }

  try {
    const res = await fetch(
      `${API_BASE}/customers/update-password`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: customer.username,   // 🔴 backend bunu istiyor
          newPassword,
        }),
      }
    );

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text);
    }

    alert("✅ Şifre başarıyla güncellendi");
  } catch (err) {
    console.error("❌ Reset password error:", err);
    alert("Şifre sıfırlanırken hata oluştu");
  }
}


  // Silme
  async function handleDelete(id) {
    if (!window.confirm("Bu müşteriyi silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${API_BASE}/customers/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomers((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error("❌ Delete customer error:", err);
    }
  }

  return (
    <div className="p-8 min-h-screen bg-gray-50">
      {/* Üst bar */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Sahadaki Toplam Gateway Sayıları</h2>
        <button
          onClick={() => {
            setEditCustomer(null);
            setShowForm(true);
          }}
          className="flex items-center gap-2 bg-indigo-500 text-white px-4 py-2 rounded-md hover:bg-indigo-600"
        >
          <Plus className="w-4 h-4" /> Yeni Müşteri
        </button>
      </div>

      {/* Model bazlı özet */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {Object.entries(modelCounts).map(([model, count]) => (
          <div
            key={model}
            className="flex bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition"
          >
            <div className="bg-indigo-500 flex items-center justify-center w-20">
              <Box className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-gray-500 text-sm">{model}</p>
              <p className="text-2xl font-bold text-gray-800">{count}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Müşteri kartları */}
      <h2 className="text-2xl font-bold mb-6">Müşteriler</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {customers
          .filter((c) => c.role !== "admin")
          .map((c) => (
            <div
              key={c.id}
              onClick={() => {
                localStorage.setItem("customerId", c.id);   // 🔑 Müşteri ID’yi kaydet
                navigate(`/admin/customers/${c.id}/gateways`);
              }}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition cursor-pointer flex flex-col relative"
            >
              {/* Menü */}
              <div className="absolute top-3 right-3">
                <CustomerMenu
                  onEdit={() => {
                    setEditCustomer(c);
                    setShowForm(true);
                  }}
                  onDelete={() => handleDelete(c.id)}
                    onResetPassword={() => handleResetPassword(c)}

                />
              </div>

              <div className="flex items-center gap-3 border-b p-4">
                <div className="bg-indigo-100 text-indigo-600 p-3 rounded-full">
                  <User className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {c.username}
                </h3>
              </div>

              <div className="flex-1 p-4 space-y-2 text-sm">
                <div className="flex items-start gap-2 text-gray-600">
                  <Mail className="w-4 h-4 mt-1" />
                  <div className="flex-1">
                    {Array.isArray(c.alarm_emails) && c.alarm_emails.length > 0 ? (
                      <div className="space-y-1">
                        {c.alarm_emails.map((email, idx) => (
                          <div key={idx} className="text-xs bg-indigo-50 px-2 py-1 rounded">
                            {email}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span>{c.contact_email || "-"}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{c.contact_phone || "-"}</span>
                </div>

                <div className="mt-4 space-y-1">
                  {modelCountsByCustomer[c.id] ? (
                    Object.entries(modelCountsByCustomer[c.id]).map(
                      ([model, info]) => (
                        <p
                          key={model}
                          className="text-xs text-gray-500 flex items-center gap-2"
                        >
                          <Server className="w-4 h-4 text-indigo-500" />
                          {model}: {info.gateways} gateway, {info.devices} cihaz
                        </p>
                      )
                    )
                  ) : (
                    <p className="text-xs text-gray-400 italic">
                      Gateway bulunamadı
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>

      {showForm && (
        <CustomerFormModal
          customer={editCustomer}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            fetchCustomers();
            setShowForm(false);
            setEditCustomer(null);
          }}
        />
      )}
    </div>
  );
}


/* -------- Menü Componenti -------- */
function CustomerMenu({ onEdit, onDelete, onResetPassword }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="p-2 rounded-full hover:bg-gray-100"
      >
        <MoreVertical className="w-5 h-5 text-gray-600" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-white border rounded-md shadow-lg z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onEdit();
            }}
            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
          >
            ✏️ Müşteriyi Düzenle
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onResetPassword();
            }}
            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
          >
            🔑 Şifre Sıfırla
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onDelete();
            }}
            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
          >
            🗑️ Müşteriyi Sil
          </button>
        </div>
      )}
    </div>
  );
}
